// Seed state for the COMMAND v2 chat prototype: two projects with believable
// chat history (including one chat created by a scheduled run) plus one
// ungrouped chat.

import { buildActivityReportContent, buildSeedPresets } from "./catalog";
import type {
  CommandProtoState,
  ProtoChatMessage,
  ProtoChatSession,
  ProtoCommandAgent,
} from "./types";

function hoursAgo(hours: number): string {
  return new Date(Date.now() - hours * 3_600_000).toISOString();
}

/** Today at the given hour (e.g. the 8am scheduled run). */
function todayAt(hour: number): string {
  const date = new Date();
  date.setHours(hour, 0, 0, 0);
  if (date.getTime() > Date.now()) date.setDate(date.getDate() - 1);
  return date.toISOString();
}

/** "19 Aug" — the day a past scheduled run landed, for run-chat titles. */
function dayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}

/** N days before the daily 8am run — keeps run history on the same clock. */
function daysBeforeRun(runIso: string, days: number): string {
  return new Date(
    new Date(runIso).getTime() - days * 24 * 3_600_000
  ).toISOString();
}

let seedCounter = 0;
function msg(
  role: "user" | "assistant",
  content: string,
  createdAt: string,
  extras?: Partial<ProtoChatMessage>
): ProtoChatMessage {
  seedCounter += 1;
  return { id: `msg_seed_${seedCounter}`, role, content, createdAt, ...extras };
}

/** The two configured Command agents shown on the Agents page. */
export function buildSeedAgents(): ProtoCommandAgent[] {
  return [
    {
      id: "agent_seed_exec",
      name: "AIOS Command — Executive",
      // The built-in agent every workspace starts with — always personal.
      isDefault: true,
      createdAt: hoursAgo(24 * 30),
      role: "Executive briefings",
      description:
        "Answers leadership questions across every team — briefings, reports and the decisions that need attention.",
      persona:
        "A calm chief-of-staff. Leads with the headline, keeps numbers in context, and always ends with what needs a decision.",
      restrictions:
        "Only report figures that exist in team activity data. Never speculate on individual performance without the underlying numbers.",
      isActive: true,
      // Default agent is always personal — the card and settings lock this.
      visibility: "personal",
      llmConfig: { model: "Claude Opus 4.7", temperature: 0.2, maxTokens: 8192, timeoutSec: 120 },
      connectedApps: ["Google Calendar", "Slack"],
      // demo: one app lost its connection — the amber markers point here
      appIssues: ["Slack"],
      customApps: [],
      knowledgeBases: ["Product handbook"],
      creditLimit: { amount: 10000, period: "week" },
    },
    {
      id: "agent_seed_discovery",
      name: "Discovery Copilot",
      createdAt: hoursAgo(24 * 7),
      role: "Client discovery",
      description:
        "Walks new client briefs through the discovery cycle — summaries, methodology matching, call prep and proposal drafts.",
      persona:
        "A thorough pre-sales consultant. Separates what the client said from what they implied, and flags open questions early.",
      restrictions:
        "Never invent client requirements. Proposals stay in draft until a human approves them.",
      isActive: true,
      visibility: "personal",
      llmConfig: { model: "Claude Opus 4.7", temperature: 0.4, maxTokens: 8192, timeoutSec: 180 },
      connectedApps: ["HubSpot-style CRM"],
      customApps: ["Internal booking API"],
      knowledgeBases: ["Support policies"],
      creditLimit: { amount: 6000, period: "week" },
    },
    {
      id: "agent_seed_pipeline",
      name: "Pipeline Analyst",
      createdAt: hoursAgo(24 * 6),
      role: "Sales pipeline",
      description:
        "Watches deal movement across the CRM — stalled deals, forecast gaps and the accounts that need a nudge this week.",
      persona:
        "A sharp revenue-operations analyst. Numbers first, then the two deals most worth attention.",
      restrictions:
        "Only reference deals that exist in the CRM. Never invent close dates or amounts.",
      isActive: true,
      visibility: "team",
      // demo: the second broken connection in the roster
      appIssues: ["HubSpot-style CRM"],
      llmConfig: { model: "Claude Opus 4.7", temperature: 0.2, maxTokens: 8192, timeoutSec: 120 },
      connectedApps: ["HubSpot-style CRM"],
      customApps: [],
      knowledgeBases: [],
      creditLimit: { amount: 8000, period: "week" },
    },
    {
      id: "agent_seed_scribe",
      name: "Meeting Scribe",
      createdAt: hoursAgo(24 * 5),
      role: "Meeting notes",
      description:
        "Turns calendar meetings into action-item summaries and follows up on the owners who haven't closed theirs.",
      persona:
        "A quiet, reliable note-taker. Short summaries, clear owners, no filler.",
      restrictions:
        "Only summarise meetings the user attended. Never attribute a quote without the transcript.",
      isActive: true,
      visibility: "personal",
      llmConfig: { model: "Claude Haiku 4.5", temperature: 0.3, maxTokens: 4096, timeoutSec: 90 },
      connectedApps: ["Google Calendar"],
      customApps: [],
      knowledgeBases: ["Product handbook"],
      creditLimit: { amount: 4000, period: "week" },
    },
    {
      id: "agent_seed_triage",
      name: "Support Triage",
      createdAt: hoursAgo(24 * 4),
      role: "Ticket triage",
      description:
        "Reads the support inbox, tags urgency and routes tickets — flags the themes that keep coming back.",
      persona:
        "A calm frontline lead. Empathetic summaries, strict priorities.",
      restrictions:
        "Never reply to a customer directly — draft for review only.",
      isActive: true,
      visibility: "team",
      llmConfig: { model: "Claude Opus 4.7", temperature: 0.2, maxTokens: 8192, timeoutSec: 120 },
      connectedApps: ["Slack"],
      customApps: ["Members portal API"],
      knowledgeBases: ["Support policies", "Billing FAQ"],
      creditLimit: { amount: 10000, period: "week" },
    },
    {
      id: "agent_seed_finance",
      name: "Finance Brief",
      createdAt: hoursAgo(24 * 3),
      role: "Spend & invoices",
      description:
        "Weekly spend digest — burn against budget, unpaid invoices and the subscriptions nobody remembers ordering.",
      persona:
        "A meticulous controller. Conservative estimates, flags anything unusual.",
      restrictions:
        "Only report figures present in the billing exports. Never project cash without labelling it an estimate.",
      isActive: false,
      visibility: "personal",
      llmConfig: { model: "AIOS Lite", temperature: 0.1, maxTokens: 4096, timeoutSec: 90 },
      connectedApps: [],
      customApps: ["Billing service API"],
      knowledgeBases: ["Billing FAQ"],
      creditLimit: { amount: 3000, period: "month" },
    },
    {
      id: "agent_seed_content",
      name: "Content Drafter",
      createdAt: hoursAgo(24 * 2),
      role: "Marketing drafts",
      description:
        "Drafts newsletters, LinkedIn posts and landing-page copy in the company voice — always as drafts for review.",
      persona:
        "An upbeat brand writer. On-message, never publishes on its own.",
      restrictions:
        "Everything it writes is a draft — never send or publish without human sign-off.",
      isActive: false,
      visibility: "team",
      llmConfig: { model: "Claude Opus 4.7", temperature: 0.6, maxTokens: 8192, timeoutSec: 120 },
      connectedApps: ["Outlook"],
      customApps: [],
      knowledgeBases: ["Product handbook"],
      creditLimit: { amount: 5000, period: "week" },
    },
  ];
}

export function buildSeedState(): CommandProtoState {
  seedCounter = 0;

  const sessions: ProtoChatSession[] = [
    // --- Project: Client Discovery Copilot -------------------------------
    {
      id: "chat_seed_brief",
      title: "Freight brief — methodology match",
      projectId: "proj_discovery",
      agentId: "agent_seed_discovery",
      createdAt: hoursAgo(26),
      updatedAt: hoursAgo(25),
      messages: [
        msg(
          "user",
          "A new client brief just arrived. Summarise the requirements, budget signals and timeline into a one-page brief summary.",
          hoursAgo(26),
          // uploaded from the composer — shows up on the Files page
          { attachedFiles: [{ name: "freight-client-brief.pdf", size: 482_000 }] }
        ),
        msg(
          "assistant",
          "Brief summarised. The client needs an operations overhaul across intake and reporting, with a budget signal in the mid-range band and a hard 12-week timeline. Three requirements are explicit, two are implied by their pain points. Open questions: data access and who owns sign-off.",
          hoursAgo(26),
          // generated by the reply — shows up on the Files page
          { attachedFiles: [{ name: "brief-summary.pdf", size: 156_000 }] }
        ),
        msg("user", "Match methodology to this brief", hoursAgo(25)),
        msg(
          "assistant",
          "Best fit is the phased-delivery methodology: the brief's fixed budget and 12-week window rule out open-ended discovery, and the integration requirements match the pattern we use for operations-heavy clients. Runner-up is the pilot-first approach — worth keeping if scope negotiation stalls.",
          hoursAgo(25),
          {
            suggestions: [
              "Build a call prep sheet",
              "Compare with the runner-up approach",
              "Summarise this for the exec team",
            ],
          }
        ),
      ],
    },
    {
      id: "chat_seed_prep",
      title: "Call prep — operations director",
      projectId: "proj_discovery",
      agentId: "agent_seed_discovery",
      createdAt: hoursAgo(20),
      updatedAt: hoursAgo(20),
      messages: [
        msg(
          "user",
          "Build a discovery call prep sheet for tomorrow's call with the operations director.",
          hoursAgo(20),
          { attachedFiles: [{ name: "ops-director-meeting-notes.docx", size: 36_500 }] }
        ),
        msg(
          "assistant",
          "Call prep sheet ready. Top questions: current process ownership, volume peaks, and what 'success in 90 days' looks like to them. Stakeholders to confirm: operations lead and finance sign-off. Risks to probe: an incumbent supplier relationship and an unclear data-access policy.",
          hoursAgo(20),
          {
            attachedFiles: [{ name: "call-prep-sheet.docx", size: 64_200 }],
            suggestions: [
              "Analyse the discovery call notes",
              "Add pricing questions",
              "Turn this into a summary email",
            ],
          }
        ),
      ],
    },
    {
      id: "chat_seed_daily",
      title: "Daily briefing — this morning",
      projectId: "proj_discovery",
      fromSchedule: true,
      // runs through the default agent — its settings page lists this job
      agentId: "agent_seed_exec",
      schedule: {
        cadenceLabel: "daily",
        time: "08:00",
        prompt:
          "Daily briefing — new briefs, pipeline movement and priorities for today.",
      },
      createdAt: todayAt(8),
      updatedAt: todayAt(8),
      messages: [
        msg(
          "user",
          "Daily briefing — new briefs, pipeline movement and priorities for today.",
          todayAt(8)
        ),
        msg(
          "assistant",
          "Good morning. Two new briefs arrived overnight — one matches the phased-delivery pattern and is worth fast-tracking. Pipeline: one proposal moved to commit, nothing stalled. Priority today: the discovery call at 2pm — the prep sheet is in your Call prep chat.",
          todayAt(8),
          {
            attachedFiles: [{ name: "daily-briefing.pdf", size: 148_000 }],
            suggestions: [
              "Summarise the fast-track brief",
              "Go deeper on the pipeline movement",
              "Adjust the schedule",
            ],
          }
        ),
      ],
    },
    // Past runs of the daily briefing — each one its own chat pointing back
    // at the schedule's anchor, so its history shows a run per day.
    {
      id: "chat_seed_daily_run1",
      title: `Daily briefing — ${dayLabel(daysBeforeRun(todayAt(8), 1))}`,
      projectId: "proj_discovery",
      fromSchedule: true,
      scheduleSourceId: "chat_seed_daily",
      agentId: "agent_seed_exec",
      createdAt: daysBeforeRun(todayAt(8), 1),
      updatedAt: daysBeforeRun(todayAt(8), 1),
      messages: [
        msg(
          "user",
          "Daily briefing — new briefs, pipeline movement and priorities for today.",
          daysBeforeRun(todayAt(8), 1)
        ),
        msg(
          "assistant",
          "Morning. Quiet on new briefs; the freight brief moved to methodology matching. Pipeline: two proposals awaiting signature, one nudged. Priority today: prep for tomorrow's discovery call and clear the complaints backlog review.",
          daysBeforeRun(todayAt(8), 1)
        ),
      ],
    },
    {
      id: "chat_seed_daily_run2",
      title: `Daily briefing — ${dayLabel(daysBeforeRun(todayAt(8), 2))}`,
      projectId: "proj_discovery",
      fromSchedule: true,
      scheduleSourceId: "chat_seed_daily",
      agentId: "agent_seed_exec",
      createdAt: daysBeforeRun(todayAt(8), 2),
      updatedAt: daysBeforeRun(todayAt(8), 2),
      messages: [
        msg(
          "user",
          "Daily briefing — new briefs, pipeline movement and priorities for today.",
          daysBeforeRun(todayAt(8), 2)
        ),
        msg(
          "assistant",
          "Morning. One new brief arrived — a freight operations overhaul, summarised and waiting in the project. Pipeline: no movement. Priority today: confirm attendees for Thursday's call and chase the two overdue proposal reviews.",
          daysBeforeRun(todayAt(8), 2)
        ),
      ],
    },
    {
      id: "chat_seed_daily_run3",
      title: `Daily briefing — ${dayLabel(daysBeforeRun(todayAt(8), 3))}`,
      projectId: "proj_discovery",
      fromSchedule: true,
      scheduleSourceId: "chat_seed_daily",
      agentId: "agent_seed_exec",
      createdAt: daysBeforeRun(todayAt(8), 3),
      updatedAt: daysBeforeRun(todayAt(8), 3),
      messages: [
        msg(
          "user",
          "Daily briefing — new briefs, pipeline movement and priorities for today.",
          daysBeforeRun(todayAt(8), 3)
        ),
        msg(
          "assistant",
          "Morning. No new briefs. Pipeline: the renewals push closed two more accounts; one enterprise deal slipped a week. Priority today: the weekly executive briefing draft and a first pass on the support themes report.",
          daysBeforeRun(todayAt(8), 3)
        ),
      ],
    },
    {
      // a run that never happened — the schedule was paused over the weekend
      id: "chat_seed_daily_run4",
      title: `Daily briefing — ${dayLabel(daysBeforeRun(todayAt(8), 4))}`,
      projectId: "proj_discovery",
      fromSchedule: true,
      scheduleSourceId: "chat_seed_daily",
      runOutcome: "skipped",
      agentId: "agent_seed_exec",
      createdAt: daysBeforeRun(todayAt(8), 4),
      updatedAt: daysBeforeRun(todayAt(8), 4),
      messages: [
        msg(
          "user",
          "Daily briefing — new briefs, pipeline movement and priorities for today.",
          daysBeforeRun(todayAt(8), 4)
        ),
      ],
    },
    {
      // a run that started but died on a lost connection
      id: "chat_seed_daily_run5",
      title: `Daily briefing — ${dayLabel(daysBeforeRun(todayAt(8), 5))}`,
      projectId: "proj_discovery",
      fromSchedule: true,
      scheduleSourceId: "chat_seed_daily",
      runOutcome: "failed",
      agentId: "agent_seed_exec",
      createdAt: daysBeforeRun(todayAt(8), 5),
      updatedAt: daysBeforeRun(todayAt(8), 5),
      messages: [
        msg(
          "user",
          "Daily briefing — new briefs, pipeline movement and priorities for today.",
          daysBeforeRun(todayAt(8), 5)
        ),
        msg(
          "assistant",
          "This run failed before finishing — the calendar connection dropped mid-read, so the briefing couldn't be assembled. The next scheduled run will retry with the same prompt.",
          daysBeforeRun(todayAt(8), 5)
        ),
      ],
    },

    // --- Project: Weekly Reporting ----------------------------------------
    {
      id: "chat_seed_exec",
      title: "Weekly executive briefing",
      projectId: "proj_reporting",
      agentId: "agent_seed_exec",
      createdAt: hoursAgo(50),
      updatedAt: hoursAgo(50),
      messages: [
        msg(
          "user",
          "Prepare the weekly executive briefing: headline metrics, wins, risks and what needs a decision this week.",
          hoursAgo(50)
        ),
        msg(
          "assistant",
          "Executive briefing ready. Headlines: revenue pacing at 104% of plan, two enterprise deals slipped to next month, and support volume down 9% after the workflow change. Wins: the renewals push closed 11 of 12 accounts. Risks: hiring pipeline for the delivery team is thin. One decision needed: approve the Q3 capacity plan.",
          hoursAgo(50),
          {
            attachedFiles: [
              { name: "weekly-executive-briefing.pdf", size: 412_000 },
            ],
            suggestions: [
              "Schedule this weekly",
              "Expand the risks section",
              "Turn this into a slide outline",
            ],
          }
        ),
      ],
    },
    {
      id: "chat_seed_pipeline_snap",
      title: "Weekly pipeline snapshot",
      projectId: "proj_reporting",
      fromSchedule: true,
      agentId: "agent_seed_pipeline",
      schedule: {
        cadenceLabel: "weekly",
        time: "09:00",
        prompt:
          "Pipeline health snapshot — stage conversion, stalled deals and forecast confidence.",
      },
      createdAt: hoursAgo(30),
      updatedAt: hoursAgo(30),
      messages: [
        msg(
          "user",
          "Pipeline health snapshot — stage conversion, stalled deals and forecast confidence.",
          hoursAgo(30)
        ),
        msg(
          "assistant",
          "Snapshot ready. Stage conversion holds at 31% brief-to-proposal; two deals have sat in negotiation for over three weeks and want a nudge. Forecast confidence is medium-high — commit coverage is 1.4x with one large deal carrying most of the risk.",
          hoursAgo(30),
          {
            suggestions: [
              "List the stalled deals",
              "Break down forecast risk",
              "Send this to the sales channel",
            ],
          }
        ),
      ],
    },
    {
      id: "chat_seed_support_digest",
      title: "Support inbox digest",
      projectId: null,
      fromSchedule: true,
      agentId: "agent_seed_triage",
      schedule: {
        cadenceLabel: "daily",
        time: "17:00",
        prompt:
          "End-of-day support digest — new tickets by urgency, recurring themes and anything unanswered.",
      },
      createdAt: hoursAgo(18),
      updatedAt: hoursAgo(18),
      messages: [
        msg(
          "user",
          "End-of-day support digest — new tickets by urgency, recurring themes and anything unanswered.",
          hoursAgo(18)
        ),
        msg(
          "assistant",
          "Today's digest: 23 new tickets — 3 urgent (all payment-related, routed to billing), 14 normal, 6 low. Recurring theme: password-reset emails landing in spam, third day running. Two tickets from yesterday are still unanswered and past SLA — flagged to the on-call agent.",
          hoursAgo(18),
          {
            suggestions: [
              "Open the unanswered tickets",
              "Go deeper on the spam issue",
              "Send the digest to the support channel",
            ],
          }
        ),
      ],
    },
    {
      id: "chat_seed_spend_review",
      title: "Monthly spend review",
      projectId: "proj_reporting",
      fromSchedule: true,
      agentId: "agent_seed_finance",
      schedule: {
        cadenceLabel: "monthly",
        time: "07:30",
        prompt:
          "Monthly spend review — burn against budget, unpaid invoices and subscription changes.",
      },
      createdAt: hoursAgo(120),
      updatedAt: hoursAgo(120),
      messages: [
        msg(
          "user",
          "Monthly spend review — burn against budget, unpaid invoices and subscription changes.",
          hoursAgo(120)
        ),
        msg(
          "assistant",
          "Spend review ready. Burn is at 92% of budget with software spend trending 6% over — two seats on the design tool look unused. Three invoices are past due totalling a mid-four-figure amount, all with the same client. One new subscription appeared this month; nobody has claimed it yet.",
          hoursAgo(120),
          {
            attachedFiles: [{ name: "monthly-spend-review.xlsx", size: 96_800 }],
            suggestions: [
              "Chase the overdue invoices",
              "List the unused seats",
              "Schedule this monthly",
            ],
          }
        ),
      ],
    },
    {
      id: "chat_seed_activity",
      title: "Agent activity — last week",
      projectId: "proj_reporting",
      agentId: "agent_seed_exec",
      createdAt: hoursAgo(74),
      updatedAt: hoursAgo(74),
      messages: [
        msg(
          "user",
          "Run the agent activity report for the last 7 days across all teams.",
          hoursAgo(74)
        ),
        msg("assistant", buildActivityReportContent(), hoursAgo(74), {
          attachedFiles: [{ name: "agent-activity-report.xlsx", size: 88_400 }],
          suggestions: [
            "Schedule this weekly",
            "Break down by agent",
            "Export as PDF",
          ],
        }),
      ],
    },

    // --- Other chats -------------------------------------------------------
    {
      id: "chat_seed_complaints",
      title: "Complaints themes — this week",
      projectId: null,
      agentId: "agent_seed_triage",
      createdAt: hoursAgo(96),
      updatedAt: hoursAgo(96),
      messages: [
        msg(
          "user",
          "Analyse this week's complaints inbox and group the emails into themes with counts.",
          hoursAgo(96)
        ),
        msg(
          "assistant",
          "I went through 47 complaint emails from the last 7 days and grouped them into 4 themes: delivery delays (19), billing confusion (12), onboarding friction (9) and feature requests filed as complaints (7). Delivery delays are up 30% on the previous week and cluster around two carriers. The other themes are flat or improving.",
          hoursAgo(96),
          {
            attachedFiles: [
              { name: "complaints-themes-breakdown.xlsx", size: 74_300 },
            ],
            suggestions: [
              "Draft replies to these emails",
              "Go deeper on the top issue",
              "Schedule this weekly",
            ],
          }
        ),
      ],
    },
  ];

  return {
    presets: buildSeedPresets(),
    projects: [
      { id: "proj_discovery", name: "Client Discovery Copilot" },
      { id: "proj_reporting", name: "Weekly Reporting" },
    ],
    sessions,
    agents: buildSeedAgents(),
    // fresh states already carry every seed agent — no merge needed later
    seededAgentsV2: true,
    activeSessionId: null,
    draftProjectId: null,
    // one agent is always picked — the Executive is the default
    activeAgentId: "agent_seed_exec",
  };
}
